import PerfectScrollbar from "react-perfect-scrollbar";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";

import { toggleSidebar } from "../../store/themeConfigSlice";
import { menuItems } from "../../lib/constants";
import { IsAccessableComponent } from "../IsAuthenticated";
import {
  IconChevronDown,
  IconSearch,
  IconGauge,
  IconX,
} from "@tabler/icons-react";
import { Accordion, TextInput, Tooltip } from "@mantine/core";
import { IRootState } from "../../store";

interface MenuItem {
  title: string;
  link?: string;
  icon?: React.ReactNode;
  submenu?: MenuItem[];
}

// Logo Component
const SidebarLogo = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  return (
    <div className="flex justify-between items-center px-3 py-3 border-b border-gray-200 dark:border-gray-700 min-h-[60px]">
      <NavLink
        to="/"
        className="flex items-center space-x-2 group min-w-0 flex-1"
      >
        <div className="flex-shrink-0">
          <img
            className="w-7  p-1 rounded-md shadow-sm transition-transform duration-200 group-hover:scale-105"
            src="https://images.sharepal.in/about-us/logo-p-blue.svg"
            alt="SharePal Logo"
          />

        </div>
        <span className="text-base font-semibold text-gray-900 dark:text-white truncate">
          {t("SharePal")}
        </span>
      </NavLink>

      <Tooltip label="Collapse sidebar" position="right" withArrow>
        <button
          type="button"
          className="p-1  hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors flex-shrink-0"
          onClick={() => dispatch(toggleSidebar())}
        >
          <IconX className="w-4 h-4" />
        </button>
      </Tooltip>
    </div>
  );
};

// Search Component
const SidebarSearch = ({
  searchTerm,
  onSearchChange,
}: {
  searchTerm: string;
  onSearchChange: (value: string) => void;
}) => {
  const { t } = useTranslation();

  return (
    <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
      <div className="relative">
        <TextInput
          placeholder={t("Search menu...") as string}
          value={searchTerm}
          onChange={(event) => onSearchChange(event.currentTarget.value)}
          size="sm"
          classNames={{
            input:
              "pl-8 pr-3 py-2.5 w-full text-xs  border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 placeholder-gray-400 focus:ring-1 dark:text-gray-100 focus:ring-blue-500 focus:border-blue-500",
          }}
        />
        <IconSearch className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 transform -translate-y-1/2 pointer-events-none" />
      </div>
    </div>
  );
};

// Dashboard Link Component
const DashboardLink = ({ pathname }: { pathname: string }) => {
  const { t } = useTranslation();
  const isActive = pathname === "/";

  return (
    <li>
      <NavLink
        to="/"
        className={`flex items-center px-3 py-2 text-xs font-medium d  mb-0.5 transition-colors min-w-0 ${isActive
            ? "bg-blue-500 text-white shadow-sm"
            : "text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
          }`}
      >
        <div
          className={`p-1  mr-2 flex-shrink-0 ${isActive ? "bg-white/20" : "bg-gray-200 dark:bg-gray-700"
            }`}
        >
          <IconGauge className="w-3.5 h-3.5" />
        </div>
        <span className="truncate">{t("dashboard")}</span>
      </NavLink>
    </li>
  );
};

// Menu Item Link Component
const MenuItemLink = ({
  menuItem,
  pathname,
  onClick,
}: {
  menuItem: MenuItem;
  pathname: string;
  onClick: () => void;
}) => {
  const { t } = useTranslation();
  const isActive = pathname === menuItem.link;

  return (
    <li>
      <NavLink
        to={menuItem.link || "#"}
        className={`flex items-center px-5 py-1  text-xs font-medium mb-0.5 transition-colors min-w-0 ${isActive
            ? "bg-blue-500 text-white shadow-sm"
            : "text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
          }`}
        onClick={onClick}
      >
        {menuItem.icon && (
          <div
            className={`p-1  mr-1 flex-shrink-0 ${isActive ? "bg-white/20" : "bg-gray-200 dark:bg-gray-700"
              }`}
          >
            <span className="w-3.5 h-2.5 flex items-center justify-center text-xs">
              {menuItem.icon}
            </span>
          </div>
        )}
        <span className="truncate">{t(menuItem.title)}</span>
      </NavLink>
    </li>
  );
};

// Submenu Item Component
const SubmenuItem = ({
  subMenuItem,
  pathname,
}: {
  subMenuItem: MenuItem;
  pathname: string;
}) => {
  const { t } = useTranslation();
  const isActive = pathname === subMenuItem.link;

  return (
    <IsAccessableComponent path={subMenuItem.link || ""} type={"sidebar"}>
      <li>
        <NavLink
          to={subMenuItem.link || "#"}
          className={`flex items-center py-2.5 px-5 text-xs  mb-0.5 transition-colors relative min-w-0 ${isActive
              ? "bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400"
              : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100"
            }`}
        >
          {subMenuItem.icon && (
            <span className="mr-2 opacity-70 flex-shrink-0 text-[10px]">
              {subMenuItem.icon}
            </span>
          )}
          <span className="truncate">{t(subMenuItem.title)}</span>
          {isActive && (
            <div className="absolute right-2 w-1.5 mr-3 h-1.5 bg-blue-500 rounded-full flex-shrink-0"></div>
          )}
        </NavLink>
      </li>
    </IsAccessableComponent>
  );
};

const Sidebar = () => {
  const themeConfig = useSelector((state: IRootState) => state.themeConfig);
  const semidark = useSelector(
    (state: IRootState) => state.themeConfig.semidark
  );
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [activeAccordionItem, setActiveAccordionItem] = useState<string | null>(
    null
  );
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filteredMenuItems, setFilteredMenuItems] =
    useState<MenuItem[]>(menuItems);

  const { pathname } = useLocation();

  // Set active accordion item based on current path
  useEffect(() => {
    let foundActiveTitle: string | null = null;
    menuItems.forEach((menuItem) => {
      if (menuItem.submenu) {
        menuItem.submenu.forEach((subMenuItem) => {
          if (subMenuItem.link === pathname) {
            foundActiveTitle = menuItem.title;
          }
        });
      } else if ("link" in menuItem && menuItem.link === pathname) {
        foundActiveTitle = menuItem.title;
      }
    });
    setActiveAccordionItem(foundActiveTitle);
  }, [pathname]);

  // Close sidebar on mobile when navigating
  useEffect(() => {
    if (window.innerWidth < 1024 && themeConfig.sidebar) {
      dispatch(toggleSidebar());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  // Filter menu items based on search
  useEffect(() => {
    if (searchTerm === "") {
      setFilteredMenuItems(menuItems);
      return;
    }

    const lowerCaseSearchTerm = searchTerm.toLowerCase();
    const newFilteredMenuItems = menuItems
      .map((menuItem) => {
        if (menuItem.submenu) {
          const filteredSubmenu = menuItem.submenu.filter((subMenuItem) =>
            t(subMenuItem.title).toLowerCase().includes(lowerCaseSearchTerm)
          );
          if (filteredSubmenu.length > 0) {
            return { ...menuItem, submenu: filteredSubmenu };
          }
        }
        if (
          t(menuItem.title).toLowerCase().includes(lowerCaseSearchTerm) &&
          !menuItem.submenu
        ) {
          return menuItem;
        }
        return null;
      })
      .filter(Boolean) as MenuItem[];
    setFilteredMenuItems(newFilteredMenuItems);
  }, [searchTerm, t]);

  return (
    <div className={semidark ? "dark" : ""}>
      <nav className="sidebar fixed top-0 left-0 h-screen w-64 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 shadow-lg z-50 flex flex-col overflow-hidden">
        {/* Logo Section */}
        <SidebarLogo />

        {/* Search Section */}
        <SidebarSearch searchTerm={searchTerm} onSearchChange={setSearchTerm} />

        {/* Scrollable Menu Area */}
        <div className="flex-1 overflow-hidden">
          <PerfectScrollbar className="h-full">
            <div className="py-2">
              <ul className="space-y-0.5">
                {/* Dashboard Section */}
                <DashboardLink pathname={pathname} />

                {/* Divider */}
                <li className="py-1">
                  <hr className="border-gray-200 dark:border-gray-700" />
                </li>

                {/* Menu Items */}
                <Accordion
                  onChange={(value: string | null) =>
                    setActiveAccordionItem(value)
                  }
                  value={activeAccordionItem}
                  variant="filled"
                  chevron={<IconChevronDown className="w-3.5 h-3.5" />}
                  classNames={{
                    item: "border-none mb-0.5",
                    control:
                      "py-2 hover:bg-gray-100  dark:bg-gray-950 dark:hover:bg-gray-800 text-xs font-medium  transition-colors min-h-0",
                    label:
                      "text-gray-700 dark:text-gray-200 flex items-center py-2 font-medium min-w-0",
                    content: "py-1 px-0",
                    chevron: "text-gray-400 transition-transform flex-shrink-0",
                    panel: "bg-gray-50 dark:bg-gray-800  mb-0.5 py-1",
                  }}
                >
                  {filteredMenuItems.map((menuItem: MenuItem) =>
                    menuItem.submenu ? (
                      <Accordion.Item
                        key={menuItem.title}
                        value={menuItem.title}
                      >
                        <Accordion.Control>
                          {menuItem.icon && (
                            <div className="p-1   bg-gray-200 dark:bg-gray-700 flex-shrink-0">
                              <span className="w-3.5 h-2.5 flex items-center justify-center text-[10px]">
                                {menuItem.icon}
                              </span>
                            </div>
                          )}
                          <span className="truncate font-semibold">{t(menuItem.title)}</span>
                        </Accordion.Control>
                        <Accordion.Panel>
                          <ul className="space-y-0.5">
                            {menuItem.submenu.map(
                              (subMenuItem: MenuItem, subIndex: number) => (
                                <SubmenuItem
                                  key={subIndex}
                                  subMenuItem={subMenuItem}
                                  pathname={pathname}
                                />
                              )
                            )}
                          </ul>
                        </Accordion.Panel>
                      </Accordion.Item>
                    ) : (
                      <MenuItemLink
                        key={menuItem.title}
                        menuItem={menuItem}
                        pathname={pathname}
                        onClick={() => setActiveAccordionItem(null)}
                      />
                    )
                  )}
                </Accordion>
              </ul>
            </div>
          </PerfectScrollbar>
        </div>

        {/* Footer Section */}
        <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex-shrink-0">
          <div className="text-center">
            <div className="text-xs font-medium text-gray-700 dark:text-gray-300">
              SharePal Admin
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              v2.0.0
            </div>
          </div>
        </div>
      </nav>
    </div>
  );
};

export default Sidebar;
